import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { skipErrorToast } from '../../interceptors/error.interceptor';
import { map, Observable } from 'rxjs';
import { buildHttpParams } from '../../utils/http-utils';
import { AccountInfo } from '../../models/account-info.model';
import { Agency, AgencyQueryParams, AgencyWithAccounts } from '../../models/agency.model';
import { Airline } from '../../models/airline.model';
import { Provider } from '../../models/provider.model';
import { AgentAccessAccount } from '../../models/agent-access-account.model';
import { Application } from '../../models/application.model';
import { ApiResponse } from '../../models/api-response.model';
import {
  StatsBreakdownItem,
  StatsBreakdownQueryParams,
  StatsQueryParams,
  StatsSummaryItem,
} from '../../models/stats';
import { AccountData, AccountDataResponse } from '../../models/account-data.model';
import { ListRuleResponseBody, RuleResponseBody } from '../../models/rule.model';
import { NotificationSettingsDataResponse } from '../../models/notification-settings.model';
import { ConsumerDataResponse } from '../../models/consumer-data.modal';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly accountUrl = environment.apiUrlV1_5 + '/agent/account';
  private readonly accessUrl = environment.apiUrlV1_5 + '/agent/access';
  private readonly statsUrl = environment.apiUrlV1_5 + '/agent/stats';
  private readonly agencyUrl = environment.apiUrlV1_5 + '/agent/agency';

  constructor(private http: HttpClient) {}

  getAccountInfo(): Observable<AccountInfo> {
    const url = `${this.accountUrl}/info`;

    return this.http
      .get<{ status: string; message: string; data: AccountInfo }>(url)
      .pipe(map(response => response.data));
  }

  getAgencies(): Observable<Agency[]> {
    const url = `${this.accountUrl}/agencies`;

    return this.http
      .get<{ Status: string; data: Agency[] }>(url)
      .pipe(map(response => response.data));
  }

  getAirlines(): Observable<{ [code: string]: Airline }> {
    const url = `${this.accountUrl}/airlines`;
    return this.http.get<{ [code: string]: Airline }>(url);
  }

  getProviders(): Observable<Provider[]> {
    const url = `${this.accountUrl}/providers`;
    return this.http.get<Provider[]>(url);
  }

  getConsumerData(): Observable<ConsumerDataResponse> {
    const url = `${this.accountUrl}/consumer`;
    return this.http.get<ConsumerDataResponse>(url);
  }

  getAccountData(): Observable<AccountDataResponse> {
    const url = `${this.accountUrl}/data`;
    return this.http.get<AccountDataResponse>(url);
  }

  getNotificationSettings(): Observable<NotificationSettingsDataResponse> {
    const url = `${this.accountUrl}/notification-settings`;
    return this.http.get<NotificationSettingsDataResponse>(url);
  }

  updateAccountData(payload: AccountData): Observable<ApiResponse> {
    const url = `${this.accountUrl}/data`;
    return this.http.post<ApiResponse>(url, payload);
  }

  registerAgency(payload: AccountData): Observable<ApiResponse> {
    const url = `${environment.apiUrlV1_5}/agent/register-agency`;
    return this.http.post<ApiResponse>(url, payload);
  }

  toggleNotificationSettings(payload: {
    rule_id: number;
  }): Observable<{ data: { enabled: boolean }; status: string }> {
    const url = `${this.accountUrl}/notification-settings/toggle`;
    return this.http.post<{ data: { enabled: boolean }; status: string }>(url, payload);
  }

  updateAirlines(payload: { [code: string]: Airline }): Observable<ApiResponse> {
    const url = `${this.accountUrl}/airlines`;
    return this.http.post<{ status: string; message: string; meta: string }>(url, payload);
  }

  uploadLogo(file: File): Observable<ApiResponse | { [key: string]: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse | { [key: string]: string }>(
      `${this.accountUrl}/upload-logo`,
      formData,
      { context: skipErrorToast() }
    );
  }

  removeLogo(): Observable<ApiResponse | { error: string }> {
    return this.http.post<ApiResponse | { error: string }>(`${this.accountUrl}/remove-logo`, '', {
      context: skipErrorToast(),
    });
  }

  getAgentAccessAccounts(): Observable<AgentAccessAccount[]> {
    const url = `${this.accessUrl}/account/index`;
    return this.http.get<AgentAccessAccount[]>(url);
  }

  getAgenciesWithAccounts(paramsObj: AgencyQueryParams): Observable<AgencyWithAccounts[]> {
    const params = buildHttpParams(paramsObj);
    const url = `${this.accessUrl}/agency/list`;
    return this.http.get<AgencyWithAccounts[]>(url, { params });
  }

  getApplications(includeAnalyticsHeader = false): Observable<Application[]> {
    const url = `${this.accessUrl}/application/list`;

    const options = includeAnalyticsHeader
      ? { headers: new HttpHeaders({ 'Ag-Analytics': 'true' }) }
      : {};

    return this.http.get<Application[]>(url, options);
  }

  updateApplication(payload: { id: string; status: string }): Observable<ApiResponse> {
    const url = `${this.accessUrl}/application/update`;
    return this.http.post<ApiResponse>(url, payload);
  }

  requestApplication(payload: { agw_id: string }): Observable<ApiResponse> {
    const url = `${this.accessUrl}/application/request`;
    return this.http.post<ApiResponse>(url, payload);
  }

  getStatsSummary(paramsObj: StatsQueryParams): Observable<StatsSummaryItem[]> {
    const url = `${this.statsUrl}/summary`;
    const params = buildHttpParams(paramsObj);
    return this.http.get<StatsSummaryItem[]>(url, { params });
  }

  getStatsBreakdown(paramsObj: StatsBreakdownQueryParams): Observable<StatsBreakdownItem[]> {
    const url = `${this.statsUrl}/breakdown`;
    const params = buildHttpParams(paramsObj);
    return this.http.get<StatsBreakdownItem[]>(url, { params });
  }

  orderExport(payload: {
    issuingDateFrom: string;
    issuingDateTo: string;
    recipientEmails: string[];
  }): Observable<{ ok: boolean }> {
    const url = `${environment.apiUrl}/v1.2/OrderExport`;
    return this.http.post<{ ok: boolean }>(url, payload);
  }

  getAgencyRules(paramsObj: { limit: number; page: number }): Observable<ListRuleResponseBody> {
    const url = `${this.agencyUrl}/rules`;
    const params = buildHttpParams(paramsObj);
    return this.http.get<ListRuleResponseBody>(url, { params });
  }

  getAgencyRuleById(id: number): Observable<RuleResponseBody> {
    const url = `${this.agencyUrl}/rules/${id}`;
    return this.http.get<RuleResponseBody>(url);
  }

  enableRule(ruleId: number): Observable<{ data: string }> {
    const url = `${this.agencyUrl}/rules/${ruleId}/enable`;
    return this.http.post<{ data: string }>(url, {});
  }

  saveArgs(ruleId: number, args: any): Observable<RuleResponseBody> {
    const url = `${this.agencyUrl}/rules/${ruleId}/args`;
    return this.http.post<RuleResponseBody>(url, args);
  }

  resetArgs(ruleId: number): Observable<{ data: string }> {
    const url = `${this.agencyUrl}/rules/${ruleId}/reset-args`;
    return this.http.post<{ data: string }>(url, {});
  }

  disableRule(ruleId: number): Observable<{ data: string }> {
    const url = `${this.agencyUrl}/rules/${ruleId}/disable`;
    return this.http.post<{ data: string }>(url, {});
  }
}
